import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { TrendingUp, DollarSign, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useCurrency } from "@/hooks/use-currency";
import { useRealtimeSubscription } from "@/hooks/use-realtime";

export const EnhancedAnalytics = () => {
  const { convertAmount, currency, formatAmount } = useCurrency();
  
  // Enable realtime updates
  useRealtimeSubscription("subscriptions", "client-subscriptions");
  useRealtimeSubscription("invoices", "client-invoices");
  useRealtimeSubscription("projects", "client-projects");

  const { data: stats, isLoading } = useQuery({
    queryKey: ["client-analytics-enhanced", currency],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get projects
      const { data: projects } = await supabase
        .from("projects")
        .select("*, customer_id")
        .eq("customer_id", user.id);

      // Get payments (subscriptions)
      const { data: payments } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id);

      // Get invoices (exclude draft/cancelled)
      const { data: invoices } = await supabase
        .from("invoices")
        .select("*")
        .eq("client_email", user.email)
        .neq("status", "draft")
        .neq("status", "cancelled");

      // Calculate metrics - exclude invoice-type subscriptions to avoid double counting
      const paymentsTotal = payments?.filter(p => p.payment_type !== 'invoice').reduce((sum, p) => {
        const sourceCurrency = (p.currency as any) || "USD";
        return sum + convertAmount(Number(p.price), sourceCurrency, currency);
      }, 0) || 0;
      
      const invoicesTotal = invoices?.reduce((sum, i) => {
        const sourceCurrency = (i.currency as any) || "USD";
        return sum + convertAmount(Number(i.total_amount), sourceCurrency, currency);
      }, 0) || 0;
      
      const totalSpent = paymentsTotal + invoicesTotal;
      const activeProjects = projects?.filter(p => p.status === 'in_progress').length || 0;
      const completedProjects = projects?.filter(p => p.status === 'completed').length || 0;
      const pendingInvoices = invoices?.filter(i => i.status === 'sent').length || 0;

      // Project status breakdown
      const statusData = [
        { name: 'Active', value: activeProjects, color: '#667eea' },
        { name: 'Completed', value: completedProjects, color: '#10b981' },
        { name: 'Planning', value: projects?.filter(p => p.status === 'planning').length || 0, color: '#f59e0b' },
      ];

      // Monthly spending - combine payments and invoices (exclude invoice-type subscriptions to avoid double counting)
      const allTransactions = [
        ...(payments?.filter(p => p.payment_type !== 'invoice').map(p => ({
          date: new Date(p.created_at),
          amount: Number(p.price),
          currency: (p.currency as any) || "USD"
        })) || []),
        ...(invoices?.map(i => ({
          date: new Date(i.created_at),
          amount: Number(i.total_amount),
          currency: (i.currency as any) || "USD"
        })) || [])
      ];
      
      const monthlyData = allTransactions.reduce((acc: any[], transaction) => {
        const month = transaction.date.toLocaleDateString('en', { month: 'short' });
        const existing = acc.find(d => d.month === month);
        const converted = convertAmount(transaction.amount, transaction.currency, currency);
        if (existing) {
          existing.amount += converted;
        } else {
          acc.push({ month, amount: converted });
        }
        return acc;
      }, []);

      return {
        totalSpent,
        activeProjects,
        completedProjects,
        pendingInvoices,
        statusData,
        monthlyData,
        projects: projects || [],
      };
    },
  });

  if (isLoading) {
    return <div className="text-center py-12">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
        <p className="text-muted-foreground mt-1">Track your project performance and spending</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Spent</p>
              <p className="text-2xl font-bold mt-1">{formatAmount(stats?.totalSpent || 0)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-primary opacity-80" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Projects</p>
              <p className="text-2xl font-bold mt-1">{stats?.activeProjects || 0}</p>
            </div>
            <Clock className="h-8 w-8 text-blue-500 opacity-80" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold mt-1">{stats?.completedProjects || 0}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500 opacity-80" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending Invoices</p>
              <p className="text-2xl font-bold mt-1">{stats?.pendingInvoices || 0}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-orange-500 opacity-80" />
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Spending */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Monthly Spending
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats?.monthlyData || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="amount" fill="#667eea" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Project Status */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Project Status Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={stats?.statusData || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => entry.name}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {stats?.statusData?.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Project Performance */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Project Performance</h3>
        <div className="space-y-4">
          {stats?.projects?.map((project: any) => (
            <div key={project.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <h4 className="font-medium">{project.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">{project.status}</p>
              </div>
              {project.budget && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Budget</p>
                  <p className="font-semibold">{project.currency} {Number(project.budget).toFixed(2)}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
